import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, Chip, CircularProgress, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import {
  Add, Refresh, AccountBalanceWallet, TrendingDown, TrendingUp, Savings, Speed,
  WarningAmberRounded, ChevronRight, ContentCopy, ArrowForward, CheckCircleOutline, Insights,
} from '@mui/icons-material';
import { budgetService, BudgetSummary, BudgetListItem } from '../../services/budgetService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  StatCard, TableSurface, headerCellSx, bodyRowSx, StatusDot, StatusTone, TypePill,
  TableEmptyState, ErrorState,
} from '../../components/common/pageKit';
import BudgetFormDialog from './BudgetFormDialog';

const STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'warning', SUBMITTED: 'info', APPROVED: 'info', ACTIVE: 'success',
  LOCKED: 'neutral', CLOSED: 'neutral', REJECTED: 'error',
};
const TYPE_TINT: Record<string, string> = {
  OPERATING: '#6366f1', CAPEX: '#0891b2', PROJECT: '#7c3aed', CASHFLOW: '#0284c7', REVENUE: '#059669',
};
const utilTone = (u: number | null): StatusTone => (u == null ? 'neutral' : u > 100 ? 'error' : u >= 90 ? 'warning' : 'success');

interface Row extends BudgetListItem { budget: number; actual: number; utilisation: number | null; forecast: number; }

const UtilBar: React.FC<{ value: number | null; color: string }> = ({ value, color }) => (
  <Box sx={{ minWidth: 120 }}>
    <Box sx={{ height: 7, borderRadius: 3, bgcolor: `${color}22`, overflow: 'hidden' }}>
      <Box sx={{ width: `${Math.min(Math.max(value || 0, 0), 100)}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 700, color }}>{value != null ? `${value}%` : '—'}</Typography>
  </Box>
);

const BudgetControlCenter: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;

  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [copyFor, setCopyFor] = useState<Row | null>(null);
  const [copyFy, setCopyFy] = useState('FY2027-28');
  const [copyGrowth, setCopyGrowth] = useState('5');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([budgetService.summary(), budgetService.list()])
      .then(async ([sum, list]) => {
        setSummary(sum);
        const enriched = await Promise.all((list.budgets || []).map(async (b) => {
          try {
            const va = await budgetService.vsActual(b.id, 'account', false);
            return { ...b, budget: va.totals.budget, actual: va.totals.actual, forecast: va.totals.forecast, utilisation: va.totals.budget ? Math.round((va.totals.actual / va.totals.budget) * 10000) / 100 : null };
          } catch { return { ...b, budget: 0, actual: 0, forecast: 0, utilisation: null }; }
        }));
        setRows(enriched); setError(null);
      })
      .catch((e) => setError(e.message || 'Failed to load budgets'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const kpis = summary ? [
    { title: 'Total Budget', color: '#6366f1', icon: <AccountBalanceWallet />, value: formatIndianNumber(summary.totalBudget), footer: <Typography variant="caption" color="text.secondary">{summary.activeCount} active budget{summary.activeCount === 1 ? '' : 's'}</Typography> },
    { title: 'Actual (posted)', color: '#0284c7', icon: <Insights />, value: formatIndianNumber(summary.actual), footer: <Typography variant="caption" color="text.secondary">from posted journals</Typography> },
    { title: 'Remaining', color: summary.remaining >= 0 ? '#059669' : '#dc2626', icon: <Savings />, value: formatIndianNumber(summary.remaining), footer: <StatusDot label={summary.remaining >= 0 ? 'Within budget' : 'Overspent'} tone={summary.remaining >= 0 ? 'success' : 'error'} /> },
    { title: 'Utilisation', color: (summary.utilisation || 0) > 100 ? '#dc2626' : '#7c3aed', icon: <Speed />, value: summary.utilisation != null ? `${summary.utilisation}%` : '—', footer: <Typography variant="caption" color="text.secondary">of total budget</Typography> },
    { title: 'Forecast @ Completion', color: '#d97706', icon: summary.forecastAtCompletion > summary.totalBudget ? <TrendingUp /> : <TrendingDown />, value: formatIndianNumber(summary.forecastAtCompletion), footer: <StatusDot label={summary.forecastAtCompletion > summary.totalBudget ? 'Trending over' : 'On track'} tone={summary.forecastAtCompletion > summary.totalBudget ? 'warning' : 'success'} /> },
    { title: 'Variance', color: summary.variance >= 0 ? '#059669' : '#dc2626', icon: summary.variance >= 0 ? <TrendingUp /> : <TrendingDown />, value: formatIndianNumber(summary.variance), footer: <Typography variant="caption" color="text.secondary">budget − actual</Typography> },
  ] : [];

  const exceptions = summary?.exceptions || [];
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const doCopy = async () => {
    if (!copyFor) return;
    try {
      await budgetService.copy(copyFor.id, { targetFinancialYear: copyFy, growthPct: Number(copyGrowth) || 0 });
      setToast('Budget copied to ' + copyFy); setCopyFor(null); load();
    } catch (e: any) { setToast(e.message || 'Copy failed'); }
  };

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Budget Control Center</Typography>
          <Typography variant="body2" color="text.secondary">Plan vs real posted actuals, run-rate forecast and exceptions across every active budget.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>New Budget</Button>
        </Stack>
      </Stack>

      {/* KPI strip */}
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {(summary ? kpis : Array.from({ length: 6 })).map((k: any, i) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
            {summary ? <StatCard title={k.title} value={k.value} icon={k.icon} color={k.color} footer={k.footer} />
              : <Paper elevation={0} sx={{ ...panelSx, height: 118, display: 'grid', placeItems: 'center' }}><CircularProgress size={20} /></Paper>}
          </Grid>
        ))}
      </Grid>

      {/* Exceptions strip + by-type */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Budget Exceptions</Typography>
              {summary && <Chip size="small" label={`${exceptions.length} flagged`} sx={{ height: 22, fontWeight: 700, bgcolor: exceptions.some((e) => e.severity === 'critical') ? `${c.bad}1f` : `${c.warn}1f`, color: exceptions.some((e) => e.severity === 'critical') ? c.bad : c.warn }} />}
            </Stack>
            <Stack spacing={1} sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {!summary ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={20} /></Box>
                : exceptions.length === 0
                  ? <Stack alignItems="center" spacing={1} sx={{ py: 4 }}><CheckCircleOutline sx={{ fontSize: 30, color: c.good }} /><Typography variant="body2" sx={{ fontWeight: 600 }}>All within tolerance</Typography><Typography variant="caption" color="text.secondary">No budget line is over 80% utilised.</Typography></Stack>
                  : exceptions.map((ex, i) => {
                    const tint = ex.severity === 'critical' ? c.bad : ex.severity === 'warning' ? c.warn : c.accent;
                    return (
                      <Stack key={i} direction="row" alignItems="center" spacing={1.25}
                        sx={{ p: 1.25, borderRadius: 2, border: line, cursor: 'pointer', '&:hover': { bgcolor: c.panel2 } }}
                        onClick={() => navigate(`/finance/budgets/${ex.budgetId}`)}>
                        <WarningAmberRounded sx={{ fontSize: 18, color: tint, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.title}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{ex.detail}</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>{formatIndianNumber(ex.amount)}</Typography>
                        <ChevronRight sx={{ fontSize: 18, color: c.sub }} />
                      </Stack>
                    );
                  })}
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1.5 }}>By Budget Type</Typography>
            <Stack spacing={1.25}>
              {(summary?.byType || []).map((t) => {
                const util = t.budget ? Math.round((t.actual / t.budget) * 100) : 0;
                const tint = TYPE_TINT[t.type] || c.accent;
                return (
                  <Box key={t.type}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{t.type} <Typography component="span" variant="caption" color="text.secondary">· {t.count}</Typography></Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>{formatIndianNumber(t.actual)} / {formatIndianNumber(t.budget)}</Typography>
                    </Stack>
                    <Box sx={{ height: 7, borderRadius: 3, bgcolor: `${tint}22`, overflow: 'hidden' }}>
                      <Box sx={{ width: `${Math.min(util, 100)}%`, height: '100%', bgcolor: tint }} />
                    </Box>
                  </Box>
                );
              })}
              {summary && !summary.byType.length && <Typography variant="body2" color="text.secondary">No active budgets.</Typography>}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={load} /></Box>}

      {/* Budget list */}
      <TableSurface>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Budget', 'FY', 'Type', 'Owner', 'Budget', 'Actual', 'Utilisation', 'Forecast', 'Status', 'Actions'].map((h, i) => (
                  <TableCell key={h} sx={headerCellSx} align={i >= 4 && i <= 7 ? 'right' : i === 9 ? 'right' : 'left'}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !rows.length ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
              ) : !rows.length ? (
                <TableRow><TableCell colSpan={10} sx={{ border: 0 }}><TableEmptyState title="No budgets yet" hint="Create your first budget to start tracking plan vs actual." /></TableCell></TableRow>
              ) : rows.map((b) => {
                const tint = TYPE_TINT[b.budgetType || 'OPERATING'] || c.accent;
                return (
                  <TableRow key={b.id} hover sx={{ ...(bodyRowSx as any), cursor: 'pointer' }} onClick={() => navigate(`/finance/budgets/${b.id}`)}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{b.name}</Typography>{b.version && b.version > 1 ? <Typography variant="caption" color="text.secondary">v{b.version}</Typography> : null}</TableCell>
                    <TableCell><Typography variant="body2">{b.financialYear}</Typography></TableCell>
                    <TableCell><TypePill label={b.budgetType || 'OPERATING'} tint={tint} /></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{b.owner || '—'}</Typography></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(b.budget)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(b.actual)}</TableCell>
                    <TableCell align="right"><Box sx={{ display: 'inline-block' }}><UtilBar value={b.utilisation} color={c[utilTone(b.utilisation) === 'error' ? 'bad' : utilTone(b.utilisation) === 'warning' ? 'warn' : 'good']} /></Box></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(b.forecast)}</TableCell>
                    <TableCell><StatusDot label={b.status} tone={STATUS_TONE[b.status] || 'neutral'} /></TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Copy to next year"><IconButton size="small" onClick={() => { setCopyFor(b); }}><ContentCopy sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                        <Tooltip title="Open Budget 360"><IconButton size="small" onClick={() => navigate(`/finance/budgets/${b.id}`)}><ArrowForward sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      <BudgetFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={() => { setToast('Budget created'); load(); }} />

      {/* Copy dialog */}
      <Dialog open={!!copyFor} onClose={() => setCopyFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Copy “{copyFor?.name}”</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select size="small" label="Target financial year" value={copyFy} onChange={(e) => setCopyFy(e.target.value)}>
              {['FY2025-26', 'FY2026-27', 'FY2027-28', 'FY2028-29'].map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </TextField>
            <TextField size="small" type="number" label="Growth %" value={copyGrowth} onChange={(e) => setCopyGrowth(e.target.value)} helperText="Each line's amount is scaled by this %." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={doCopy}>Copy as Draft</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default BudgetControlCenter;
